import React, { useState, useEffect, useCallback, useRef, useImperativeHandle } from 'react';
import { FaSearch, FaUndo, FaFilter, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import SelectField from '../../../../common/Input/SelectField';
// AdvancedFilterControls non è più importato direttamente qui, ma tramite filterConfig

export const GenericFilterPanel = React.forwardRef(({
  filterConfig,
  filters,
  onFilterChange,
  onSearch,
  onReset,
  theme,
  lookupOptions,
  isLoadingLookups,
}, ref) => {
  const [isOpen, setIsOpen] = useState(true);
  const [localTextFilters, setLocalTextFilters] = useState({});

  // NUOVO: Esponi una funzione per ottenere i filtri locali tramite ref
  useImperativeHandle(ref, () => ({
    getLocalTextFilters: () => {
      return localTextFilters;
    }
  }));

  const isTypingRef = useRef(false); // Traccia se l'utente sta digitando
  const typingTimeoutRef = useRef(null); // Timeout per rilevare fine digitazione

  // Estrai themeColor dal tema
  const themeColor = theme?.themeColor || 'emerald';

  // CORRETTO: Sincronizza i filtri di testo locali con quelli globali
  useEffect(() => {
    // Non sincronizzare se l'utente sta digitando
    if (isTypingRef.current) {
      return;
    }

    const initialTextValues = {};
    filterConfig.forEach(fc => {
      if (fc.type === 'text') {
        const likeValue = filters.hasOwnProperty(`${fc.field}_like`) ? filters[`${fc.field}_like`] : null;
        const exactValue = filters.hasOwnProperty(fc.field) ? filters[fc.field] : null;
        
        const finalValue = likeValue || exactValue || '';
        initialTextValues[fc.field] = finalValue;
      }
    });
    
    setLocalTextFilters(initialTextValues);
  }, [filters, filterConfig]);

  const handleLocalTextChange = useCallback((field, value) => {
    // Indica che l'utente sta digitando
    isTypingRef.current = true;
    
    // Resetta il timeout precedente
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Imposta un timeout per rilevare quando l'utente smette di digitare
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
    }, 1000); // 1 secondo di inattività
    
    setLocalTextFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  // CORRETTO: Gestisce la ricerca per i campi di testo
  const handleImmediateSearch = useCallback(() => {
    console.log('[GenericFilterPanel] Ricerca con filtri:', localTextFilters);
    
    // Processa tutti i campi di testo
    const textFields = filterConfig.filter(fc => fc.type === 'text').map(fc => fc.field);
    textFields.forEach(field => {
      const value = localTextFilters[field]?.trim();
      
      if (value) {
        onFilterChange(field, null); // Pulisce il filtro esatto
        onFilterChange(`${field}_like`, value); // Imposta il filtro like
      } else {
        // FIX: Se il valore è vuoto, rimuovi entrambi i filtri
        onFilterChange(field, null);
        onFilterChange(`${field}_like`, null);
      }
    });

    if (onSearch) {
      setTimeout(() => {
        console.log('[GenericFilterPanel] Esecuzione onSearch');
        onSearch();
      }, 50); // Esegue la ricerca dopo l'aggiornamento dello stato
    } else {
      console.warn('[GenericFilterPanel] onSearch non è disponibile');
    }
  }, [localTextFilters, filterConfig, onFilterChange, onSearch]);

  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleImmediateSearch();
    }
  }, [handleImmediateSearch]);

  // Resetta tutti i filtri
  const handleResetFiltersAndLocal = useCallback(() => {
    console.log('[GenericFilterPanel] Reset completo dei filtri');
    setLocalTextFilters({});
    onReset();
  }, [onReset]);

  const advancedCustomFilter = filterConfig.find(f => f.type === 'customAdvanced');
  const regularFilters = filterConfig.filter(f => f.type !== 'customAdvanced');

  return (
    <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
      {/* Header moderno con tema dinamico - CORRETTO per dark mode */}
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className={`w-full flex items-center justify-between p-3 mb-3 text-sm font-semibold text-left rounded-lg transition-all duration-200
                   bg-gradient-to-r from-${themeColor}-200 to-${themeColor}-100 dark:from-gray-900 dark:to-gray-800
                   border border-${themeColor}-300 dark:border-gray-700
                   text-${themeColor}-800 dark:text-white
                   hover:from-${themeColor}-300 hover:to-${themeColor}-200 dark:hover:from-gray-800 dark:hover:to-gray-700
                   focus:outline-none focus:ring-2 focus:ring-${themeColor}-500 focus:ring-offset-2`}
      >
        <div className="flex items-center">
          <FaFilter className={`mr-2 h-4 w-4 text-${themeColor}-700 dark:text-${themeColor}-200`} />
          Filtri di Ricerca
        </div>
        {isOpen ? (
          <FaChevronUp className={`h-4 w-4 text-${themeColor}-700 dark:text-${themeColor}-200`} />
        ) : (
          <FaChevronDown className={`h-4 w-4 text-${themeColor}-700 dark:text-${themeColor}-200`} />
        )}
      </button>

      {isOpen && (
        <>
          {/* MODIFICA CHIAVE: Renderizza dinamicamente il componente custom e passa le props */}
          {advancedCustomFilter && advancedCustomFilter.component && (
            <div className="mb-4">
              {React.createElement(advancedCustomFilter.component, {
                filters: filters,
                onFilterChange: onFilterChange,
                theme: theme,
                // CORREZIONE: Passiamo la prop corretta dalle componentProps
                onAdvancedFilterChange: advancedCustomFilter.componentProps?.onAdvancedFilterChange || advancedCustomFilter.onAdvancedFilterChange,
                onSearch: onSearch, // AGGIUNTO: passa la funzione di ricerca al componente avanzato
                // Passa tutte le props addizionali definite in `componentProps`
                ...advancedCustomFilter.componentProps,
              })}
            </div>
          )}
          
          {regularFilters.length > 0 && (
            <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4 ${advancedCustomFilter ? 'pt-4 border-t border-gray-200 dark:border-gray-700' : ''}`}>
              {regularFilters.map((filter) => {
                const filterValue = filters[filter.field] || '';
                
                if (filter.type === 'lookup' && filter.lookupType === 'select') {
                  const currentOptions = lookupOptions[filter.lookupName] || [];
                  return (
                    <div key={filter.field} className="w-full">
                      {filter.label && (
                        <label 
                          htmlFor={`filter-${filter.field}`} 
                          className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          {filter.label}
                        </label>
                      )}
                      <SelectField 
                        id={`filter-${filter.field}`} 
                        value={filterValue} 
                        onChange={(newValue) => onFilterChange(filter.field, newValue)} 
                        options={currentOptions} 
                        placeholder={`Seleziona ${filter.label || filter.field}...`} 
                        className="text-sm"
                        isLoading={isLoadingLookups && currentOptions.length === 0} 
                      />
                    </div>
                  );
                }
                if (filter.type === 'text') {
                  return (
                    <div key={filter.field} className="flex flex-col">
                      <label 
                        htmlFor={`filter-${filter.field}`} 
                        className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        {filter.label || filter.field}
                      </label>
                      <input 
                        type="text" 
                        id={`filter-${filter.field}`} 
                        value={localTextFilters[filter.field] || ''} 
                        onChange={(e) => handleLocalTextChange(filter.field, e.target.value)} 
                        onKeyPress={handleKeyPress} 
                        placeholder={`Cerca ${filter.label || filter.field}...`} 
                        className={`w-full px-3 py-2 text-sm border rounded-lg shadow-sm transition-all duration-200
                                   border-gray-300 dark:border-gray-600 
                                   bg-white dark:bg-gray-700 
                                   text-gray-900 dark:text-gray-100
                                   placeholder-gray-400 dark:placeholder-gray-500
                                   focus:outline-none focus:ring-2 focus:ring-${themeColor}-500 focus:border-${themeColor}-500
                                   hover:border-gray-400 dark:hover:border-gray-500`}
                      />
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}

          {/* Pulsanti di azione moderni */}
          <div className="flex flex-col items-center justify-end mt-6 space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
            <button 
              onClick={handleImmediateSearch} 
              className={`w-full sm:w-auto px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition-all duration-200
                         bg-${themeColor}-600 hover:bg-${themeColor}-700 
                         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${themeColor}-500 
                         flex items-center justify-center hover:scale-105 hover:shadow-md`}
            >
              <FaSearch className="w-4 h-4 mr-2" />
              Cerca
            </button>
            <button 
              onClick={handleResetFiltersAndLocal} 
              className={`w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-lg border shadow-sm transition-all duration-200
                         text-gray-700 dark:text-gray-200 
                         bg-white dark:bg-gray-700 
                         border-gray-300 dark:border-gray-600 
                         hover:bg-gray-50 dark:hover:bg-gray-600 
                         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 
                         flex items-center justify-center hover:scale-105`}
            >
              <FaUndo className="w-4 h-4 mr-2" />
              Resetta
            </button>
          </div>
        </>
      )}
    </div>
  );
});

export default GenericFilterPanel;